import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Cookies from "js-cookie";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";
import { X, Copy, Trash2, MessageSquare, Hash, Forward } from 'lucide-react';

import MessageCard from "./MessageCard";
import CreateMessage from "./addMessageToChannel";
import ForwardMessageDialog from "./ForwardMessageDialog";

import { BACKEND_API_URL } from "../utils/api";
import { setFullscreenImage, setActiveThreadParentId } from "../redux/actions/channel.action";
import { selectChannelById } from "../redux/selectors/channel.selectors";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

const REACT_APP_BACKEND_API_URL = BACKEND_API_URL;

const ThreadView = () => {
    const { channelId } = useParams();
    const location = useLocation();
    const messageId = new URLSearchParams(location.search).get('messageId');
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const currentChannel = useSelector(state => selectChannelById(state, channelId));
    const channelName = currentChannel?.name || '';
    const channelDescription = currentChannel?.description || '';
    const mobileScreen = window?.innerWidth <= 600;

    // Thread state
    const [activeThread, setActiveThread] = useState(null);
    const [parentMessageId, setParentMessageId] = useState(null);
    const [parentMessage, setParentMessage] = useState(null);
    const [threadMessages, setThreadMessages] = useState([]);
    const [selectedMessages, setSelectedMessages] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const scrollContainerRef = useRef(null);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [focusedMessageIndex, setFocusedMessageIndex] = useState(-1);
    const messageRefs = useRef({});
    const previousMessageCountRef = useRef(0);
    const lastThreadMessageIdRef = useRef(null);
    const [selectedReplyMessage, setSelectedReplyMessage] = useState(null);
    const [forwardDialogOpen, setForwardDialogOpen] = useState(false);

    // Parse messageId from URL for highlighting the searched message
    const selectedMessageId = messageId ? parseInt(messageId) : null;

    // Load thread info from URL messageId
    useEffect(() => {
        const loadThreadFromMessage = async () => {
            if (!messageId) {
                setActiveThread(null);
                setParentMessageId(null);
                setParentMessage(null);
                setThreadMessages([]);
                dispatch(setActiveThreadParentId(null));
                return;
            }

            try {
                const accessToken = Cookies.get('access_token');
                const response = await axios.get(`${REACT_APP_BACKEND_API_URL}/api/message/${messageId}/`, {
                    headers: {
                        Authorization: `Token ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (response.data) {
                    const threadId = response.data.root_thread || response.data.thread;
                    const rootMsgId = response.data.root_message_id || parseInt(messageId);

                    // Always set parent message ID and dispatch to Redux
                    setParentMessageId(rootMsgId);
                    dispatch(setActiveThreadParentId(rootMsgId));

                    if (threadId) {
                        // Message has an existing thread
                        setActiveThread(threadId);

                        // If clicked message is the root, use it directly
                        if (rootMsgId === parseInt(messageId)) {
                            setParentMessage(response.data);
                        } else {
                            // Fetch the actual root message
                            try {
                                const rootResponse = await axios.get(`${REACT_APP_BACKEND_API_URL}/api/message/${rootMsgId}/`, {
                                    headers: {
                                        Authorization: `Token ${accessToken}`,
                                        'Content-Type': 'application/json',
                                    },
                                });
                                setParentMessage(rootResponse.data);
                            } catch (err) {
                                console.error('Failed to fetch root message:', err);
                                setParentMessage(response.data); // Fallback to clicked message
                            }
                        }
                    } else {
                        // No thread yet - this is a new thread, show the message anyway
                        setActiveThread(-1); // Use -1 to indicate "no thread yet"
                        setParentMessage(response.data);
                    }
                }
            } catch (error) {
                console.error('Failed to load thread:', error);
                navigate(`/channels/${channelId}`, { replace: true });
            }
        };

        loadThreadFromMessage();
    }, [messageId, channelId, navigate]);

    // Reset state when message changes (triggered by clicking a new message)
    // This runs before loadThreadFromMessage sets the new activeThread
    useEffect(() => {
        // Reset activeThread to prevent fetch effect from fetching old thread's messages
        setActiveThread(null);
        setParentMessage(null);
        setParentMessageId(null);
        setPage(1);
        setThreadMessages([]);
        setHasMore(true);
        setIsInitialLoad(true);
        setSelectedMessages([]);
        setFocusedMessageIndex(-1);
        lastThreadMessageIdRef.current = null;
    }, [messageId]);

    // Handle Escape key to close thread
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                if (messageId) {
                    navigate(`/channels/${channelId}`);
                }
                setFocusedMessageIndex(-1);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [messageId, channelId, navigate]);

    // Fetch thread messages
    useEffect(() => {
        const fetchThreadMessages = async (pageNum) => {
            if (activeThread === null || activeThread === -1) {
                setThreadMessages([]);
                setSelectedMessages([]);
                return;
            }

            try {
                setIsLoadingMore(true);
                const accessToken = Cookies.get('access_token');
                const response = await axios.get(`${REACT_APP_BACKEND_API_URL}/api/threads/${activeThread}/messages/`, {
                    headers: {
                        'Authorization': `Token ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    params: { page: pageNum }
                });

                if (response.data && Array.isArray(response.data.results)) {
                    const reversedResults = [...response.data.results].reverse();

                    if (pageNum === 1) {
                        setThreadMessages(reversedResults);
                        setIsInitialLoad(true);
                        if (reversedResults.length > 0) {
                            lastThreadMessageIdRef.current = Math.max(...reversedResults.map(m => m.id));
                            setTimeout(() => {
                                if (scrollContainerRef.current) {
                                    scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
                                }
                            }, 0);
                        }
                    } else {
                        const scrollElement = scrollContainerRef.current;
                        const previousScrollHeight = scrollElement?.scrollHeight || 0;

                        setThreadMessages((prevMessages) => [...reversedResults, ...prevMessages]);

                        setTimeout(() => {
                            if (scrollElement) {
                                const newScrollHeight = scrollElement.scrollHeight;
                                scrollElement.scrollTop = newScrollHeight - previousScrollHeight;
                            }
                        }, 0);
                    }

                    if (response.data?.next === null) {
                        setHasMore(false);
                    }
                } else if (Array.isArray(response.data)) {
                    const reversedResults = [...response.data].reverse();
                    setThreadMessages(reversedResults);
                    setHasMore(false);
                    setIsInitialLoad(true);
                    if (reversedResults.length > 0) {
                        lastThreadMessageIdRef.current = Math.max(...reversedResults.map(m => m.id));
                    }
                }
            } catch (error) {
                toast.error('Error fetching thread messages');
            } finally {
                setIsLoadingMore(false);
            }
        };

        fetchThreadMessages(page);
    }, [page, activeThread, parentMessageId]);

    // Poll for new thread messages
    useEffect(() => {
        if (activeThread === null || activeThread === -1) return;

        const pollForNewThreadMessages = async () => {
            try {
                const accessToken = Cookies.get('access_token');
                const params = { page: 1 };
                if (lastThreadMessageIdRef.current) {
                    params.since_id = lastThreadMessageIdRef.current;
                }

                const response = await axios.get(`${REACT_APP_BACKEND_API_URL}/api/threads/${activeThread}/messages/`, {
                    headers: {
                        'Authorization': `Token ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    params
                });

                if (response.data && Array.isArray(response.data.results)) {
                    const reversedResults = [...response.data.results].reverse();
                    if (reversedResults.length > 0) {
                        setThreadMessages(prev => {
                            const existingIds = new Set(prev.map(m => m.id));
                            const newMessages = reversedResults.filter(m => !existingIds.has(m.id));
                            if (newMessages.length > 0) {
                                const updated = [...prev, ...newMessages];
                                lastThreadMessageIdRef.current = Math.max(...updated.map(m => m.id));
                                return updated;
                            }
                            return prev;
                        });
                    }
                } else if (Array.isArray(response.data)) {
                    const reversedResults = [...response.data].reverse();
                    if (reversedResults.length > 0) {
                        setThreadMessages(prev => {
                            const existingIds = new Set(prev.map(m => m.id));
                            const newMessages = reversedResults.filter(m => !existingIds.has(m.id));
                            if (newMessages.length > 0) {
                                const updated = [...prev, ...newMessages];
                                lastThreadMessageIdRef.current = Math.max(...updated.map(m => m.id));
                                return updated;
                            }
                            return prev;
                        });
                    }
                }
            } catch (error) {
                console.error('Failed to poll thread messages:', error);
            }
        };

        const interval = setInterval(pollForNewThreadMessages, 1000);
        return () => clearInterval(interval);
    }, [activeThread]);

    // Auto-scroll to bottom (or to selected message if coming from search)
    useEffect(() => {
        if (threadMessages.length > 0) {
            const scrollElement = scrollContainerRef.current;

            if (isInitialLoad) {
                // If we have a selectedMessageId and it's in the thread, scroll to it
                const shouldScrollToSelected = selectedMessageId && messageRefs.current[selectedMessageId];

                const scrollToTarget = () => {
                    if (shouldScrollToSelected) {
                        messageRefs.current[selectedMessageId].scrollIntoView({ behavior: 'smooth', block: 'center' });
                    } else {
                        const lastMessage = threadMessages[threadMessages.length - 1];
                        if (lastMessage && messageRefs.current[lastMessage.id]) {
                            messageRefs.current[lastMessage.id].scrollIntoView({ behavior: 'auto', block: 'end' });
                        } else if (scrollElement) {
                            scrollElement.scrollTop = scrollElement.scrollHeight;
                        }
                    }
                };

                scrollToTarget();
                requestAnimationFrame(() => {
                    scrollToTarget();
                    setTimeout(() => {
                        scrollToTarget();
                        setIsInitialLoad(false);
                    }, 300);
                });
            } else if (!isLoadingMore) {
                const isNewMessage = threadMessages.length > previousMessageCountRef.current;
                if (isNewMessage) {
                    requestAnimationFrame(() => {
                        const lastMessage = threadMessages[threadMessages.length - 1];
                        if (lastMessage && messageRefs.current[lastMessage.id]) {
                            messageRefs.current[lastMessage.id].scrollIntoView({ behavior: 'auto', block: 'end' });
                        } else if (scrollElement) {
                            scrollElement.scrollTop = scrollElement.scrollHeight;
                        }
                    });
                }
            }

            previousMessageCountRef.current = threadMessages.length;
        }
    }, [threadMessages, isInitialLoad, isLoadingMore, selectedMessageId]);

    // Handle scroll
    const handleScroll = (e) => {
        const scrollElement = e.target;
        if (scrollElement.scrollTop < 50 && hasMore && !isLoadingMore && !isInitialLoad) {
            setPage((prevPage) => prevPage + 1);
        }
    };

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (threadMessages.length === 0) return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setFocusedMessageIndex(prev => {
                    const nextIndex = prev < threadMessages.length - 1 ? prev + 1 : prev;
                    setTimeout(() => {
                        const msgId = threadMessages[nextIndex]?.id;
                        if (msgId && messageRefs.current[msgId]) {
                            messageRefs.current[msgId].scrollIntoView({
                                behavior: 'smooth',
                                block: 'center'
                            });
                        }
                    }, 0);
                    return nextIndex;
                });
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setFocusedMessageIndex(prev => {
                    const nextIndex = prev > 0 ? prev - 1 : 0;
                    setTimeout(() => {
                        const msgId = threadMessages[nextIndex]?.id;
                        if (msgId && messageRefs.current[msgId]) {
                            messageRefs.current[msgId].scrollIntoView({
                                behavior: 'smooth',
                                block: 'center'
                            });
                        }
                    }, 0);
                    return nextIndex;
                });
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [threadMessages]);

    // Close thread
    const handleCloseThread = () => {
        navigate(`/channels/${channelId}`);
    };

    const updateMessageStatus = (msgId) => {
        const messageIndex = threadMessages.findIndex(message => message.id === msgId);
        if (messageIndex !== -1) {
            const updatedMessages = [...threadMessages];
            updatedMessages[messageIndex] = {
                ...updatedMessages[messageIndex],
                action_taken: true,
            };
            setThreadMessages(updatedMessages);
        }
    };

    const handleDeleteMessage = (msgId) => {
        setThreadMessages(prevMessages => prevMessages.filter(msg => msg.id !== msgId));
        setSelectedMessages(prev => prev.filter(msg => msg.id !== msgId));
    };

    const handleMessageSelect = (message, event) => {
        if (event?.ctrlKey || event?.metaKey) {
            setSelectedMessages(prev => {
                const isAlreadySelected = prev.some(msg => msg.id === message.id);
                if (isAlreadySelected) {
                    return prev.filter(msg => msg.id !== message.id);
                } else {
                    // Show popup only when transitioning from exactly 30 to 31 messages
                    if (prev.length === 30) {
                        toast.error(`Maximum limit of 30 messages for forwarding reached. You can forward up to 30 messages at once.`);
                    }
                    return [...prev, message];
                }
            });
        }
    };

    const handleClearSelection = () => {
        setSelectedMessages([]);
    };

    const handleForwardClick = () => {
        if (selectedMessages.length > 30) {
            toast.error('Maximum 30 messages can be forwarded at once');
            return;
        }
        setForwardDialogOpen(true);
    };

    const handleCopySelectedToCanvas = () => {
        if (selectedMessages.length === 0) return;

        // In production, use hardcoded URL for media files served from onemap.morphlelabs.com
        const PRODUCTION_MEDIA_URL = 'https://onemap.morphlelabs.com';

        const messagesData = selectedMessages.map(message => ({
            type: 'channel_message',
            media_type: message.media_type,
            media_url: message.media_url?.startsWith('/')
                ? `${PRODUCTION_MEDIA_URL}${message.media_url}`
                : message.media_url,
            message_text: message.message || '',
            author: `${message.author.first_name} ${message.author.last_name}`,
            created_at: message.created_at,
            channel_message_id: message.id
        }));

        localStorage.setItem('copiedChannelMessages', JSON.stringify({
            messages: messagesData,
            timestamp: new Date().getTime()
        }));

        toast.success(`${selectedMessages.length} message(s) copied! Press Ctrl+V in folder canvas to create items`);
        setSelectedMessages([]);
    };

    const handleDeleteSelected = async () => {
        if (selectedMessages.length === 0) return;

        const confirmDelete = window.confirm(`Delete ${selectedMessages.length} message(s)? This action cannot be undone.`);
        if (!confirmDelete) return;

        const accessToken = Cookies.get('access_token');
        let successCount = 0;

        for (const message of selectedMessages) {
            try {
                await axios.delete(`${REACT_APP_BACKEND_API_URL}/api/message/${message.id}/delete/`, {
                    headers: {
                        'Authorization': `Token ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                });
                successCount++;
            } catch (error) {
                console.error(`Failed to delete message ${message.id}:`, error);
            }
        }

        if (successCount > 0) {
            const deletedIds = selectedMessages.slice(0, successCount).map(m => m.id);
            setThreadMessages(prevMessages => prevMessages.filter(msg => !deletedIds.includes(msg.id)));
            toast.success(`Deleted ${successCount} of ${selectedMessages.length} message(s)`);
        } else {
            toast.error('Failed to delete messages');
        }

        setSelectedMessages([]);
    };

    // Compare calendar days using toDateString() so we match formatDateSeparator (Today/Yesterday) and avoid timezone/parse issues
    const isDifferentDay = (date1, date2) => {
        if (date1 == null || date2 == null) return true;
        const d1 = new Date(date1);
        const d2 = new Date(date2);
        if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) return true;
        return d1.toDateString() !== d2.toDateString();
    };

    const formatDateSeparator = (date) => {
        const msgDate = new Date(date);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (msgDate.toDateString() === today.toDateString()) {
            return 'Today';
        } else if (msgDate.toDateString() === yesterday.toDateString()) {
            return 'Yesterday';
        } else {
            return msgDate.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        }
    };

    const shouldShowAuthor = (currentMessage, previousMessage) => {
        if (!previousMessage) return true;
        if (currentMessage.author.id !== previousMessage.author.id) return true;
        if (isDifferentDay(previousMessage.created_at, currentMessage.created_at)) return true;
        return false;
    };

    const isLastInSeries = (currentMessage, nextMessage) => {
        if (!nextMessage) return true;
        if (currentMessage.author.id !== nextMessage.author.id) return true;
        if (isDifferentDay(currentMessage.created_at, nextMessage.created_at)) return true;
        return false;
    };

    const handleMessageFocus = (message) => {
        const index = threadMessages.findIndex(msg => msg.id === message.id);
        if (index !== -1) {
            setFocusedMessageIndex(index);
        }
    };

    const handleMessageReply = (message) => {
        setSelectedReplyMessage(message);
    };

    const handleCancelReply = () => {
        setSelectedReplyMessage(null);
    };

    // Callback to add a new thread message and update lastThreadMessageIdRef
    // This prevents race conditions with the polling mechanism
    const addThreadMessage = (newMessage) => {
        setThreadMessages((prevMessages) => {
            // Check if message already exists to prevent duplicates
            if (prevMessages.some((m) => m.id === newMessage.id)) {
                return prevMessages;
            }
            return [...prevMessages, newMessage];
        });
        // Update the ref so polling won't fetch this message again
        if (newMessage.id > (lastThreadMessageIdRef.current || 0)) {
            lastThreadMessageIdRef.current = newMessage.id;
        }
    };

    const handleScrollToMessage = (messageId) => {
        if (!scrollContainerRef.current) return;

        const targetId = parseInt(messageId);

        // Check if it's the parent message
        if (parentMessage && parentMessage.id === targetId) {
            // Scroll to parent message area
            setTimeout(() => {
                const parentElement = document.querySelector(`[data-parent-message-id="${targetId}"]`);
                if (parentElement) {
                    // Use scrollIntoView for reliable scrolling
                    parentElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest'
                    });

                    // Highlight it briefly
                    setTimeout(() => {
                        parentElement.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
                        setTimeout(() => {
                            parentElement.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
                        }, 2000);
                    }, 300);
                }
            }, 100);
            return;
        }

        // Check if it's in thread messages
        const messageIndex = threadMessages.findIndex(msg => msg.id === targetId);
        if (messageIndex !== -1) {
            const messageRef = messageRefs.current[targetId];
            if (messageRef) {
                // Use scrollIntoView for reliable scrolling
                messageRef.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest'
                });

                // Focus and highlight the message
                setFocusedMessageIndex(messageIndex);
                setTimeout(() => {
                    messageRef.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
                    setTimeout(() => {
                        messageRef.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
                    }, 2000);
                }, 300);
            }
        }
    };

    // Date separator component
    const DateSeparator = ({ date }) => (
        <div className="flex items-center gap-3 my-5">
            <Separator className="flex-1" />
            <span className="text-xs font-semibold text-primary bg-background px-3 py-1 rounded-full border border-primary/30">
                {formatDateSeparator(date)}
            </span>
            <Separator className="flex-1" />
        </div>
    );

    // Empty state - no thread open
    if (!messageId) {
        return (
            <div className={cn(
                "flex flex-col items-center h-full w-full bg-muted/30 p-6",
                mobileScreen ? "justify-center" : "justify-between"
            )}>
                {mobileScreen ? (
                    <>
                        <MessageSquare className="h-24 w-24 text-muted-foreground/30 mb-4" />
                        <h3 className="text-lg font-semibold text-muted-foreground">No Thread Selected</h3>
                        <p className="text-sm text-muted-foreground/60 mt-1">Double click to select a thread</p>
                    </>
                ) : (
                    <>
                        <div className="flex-1 flex flex-col items-center justify-center">
                            <MessageSquare className="h-24 w-24 text-muted-foreground/30 mb-4" />
                            <p className="text-muted-foreground/60 italic">
                                Double click on a message to view its thread
                            </p>
                        </div>
                        <div className="flex flex-col items-center w-full">
                            <h2 className="text-2xl font-bold text-foreground flex items-center gap-2 mb-2">
                                <Hash className="h-6 w-6" />
                                {channelName || 'Channel'}
                            </h2>
                            {channelDescription && (
                                <p className="text-sm text-muted-foreground text-center max-w-md whitespace-pre-wrap">
                                    {channelDescription}
                                </p>
                            )}
                        </div>
                    </>
                )}
            </div>
        );
    }

    return (
        <div className="h-full w-full flex flex-col">
            {/* Thread Header */}
            <div className={cn(
                "flex items-center justify-between border-b shrink-0 bg-background",
                mobileScreen ? "px-3 py-2 mt-10" : "px-4 py-3"
            )}>
                <h3 className="font-semibold text-foreground">Thread Replies</h3>
                <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                        mobileScreen ? "h-10 w-10" : "h-8 w-8"
                    )}
                    onClick={handleCloseThread}
                >
                    <X className={cn(mobileScreen ? "h-5 w-5" : "h-4 w-4")} />
                </Button>
            </div>

            {/* Messages Container */}
            <div className="flex-1 min-h-0 overflow-hidden">
                <div
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    className={cn(
                        "h-full w-full overflow-auto",
                        "bg-muted/50 dark:bg-muted/30",
                        mobileScreen ? "p-1 pb-32" : "p-4 rounded-lg"
                    )}
                >
                    {/* Parent Message - Original message that started the thread */}
                    {parentMessage && (
                        <div className="mb-4">
                            <div className="flex items-center gap-2 mb-2 px-2">
                                <MessageSquare className="h-4 w-4 text-primary" />
                                <span className="text-xs font-semibold text-primary uppercase tracking-wide">
                                    Original Message
                                </span>
                            </div>
                            <div data-parent-message-id={parentMessage.id}>
                                <MessageCard
                                    message={parentMessage}
                                    isThread={true}
                                    prepareActiveThread={null}
                                    updateMessageStatus={() => { }}
                                    openFullscreenImage={(url) => dispatch(setFullscreenImage(url))}
                                    user={user}
                                    selectedMessageId={null}
                                    onDelete={() => { }}
                                    isSelected={false}
                                    isActiveThread={parentMessage.id === selectedMessageId}
                                    isThreadOpen={false}
                                    onSelect={() => { }}
                                    showAuthor={true}
                                    showAvatar={true}
                                    onFocus={() => { }}
                                    isFocused={false}
                                    mobileScreen={mobileScreen}
                                    onReply={null}
                                    isReplySelected={false}
                                    onScrollToMessage={handleScrollToMessage}
                                />
                            </div>
                            <Separator className="mt-4" />
                            <div className="flex items-center justify-center py-2">
                                <span className="text-xs text-muted-foreground">
                                    {threadMessages.length} {threadMessages.length === 1 ? 'reply' : 'replies'}
                                </span>
                            </div>
                        </div>
                    )}
                    {/* Loading indicator */}
                    {isLoadingMore && page > 1 && (
                        <div className="text-center py-3">
                            <span className="text-sm text-muted-foreground">Loading older messages...</span>
                        </div>
                    )}

                    {/* No more messages indicator */}
                    {!hasMore && threadMessages.length > 0 && !isInitialLoad && (
                        <div className="text-center py-3 mb-3">
                            <span className="text-sm text-muted-foreground italic">
                                No more messages before this
                            </span>
                        </div>
                    )}

                    {/* Messages */}
                    {threadMessages.map((message, index) => (
                        <React.Fragment key={message.id}>
                            {/* Date separator */}
                            {(index === 0 || isDifferentDay(threadMessages[index - 1].created_at, message.created_at)) && (
                                <DateSeparator date={message.created_at} />
                            )}

                            {/* Message card */}
                            <div ref={(el) => { messageRefs.current[message.id] = el; }}>
                                <MessageCard
                                    message={message}
                                    isThread={true}
                                    prepareActiveThread={null}
                                    updateMessageStatus={updateMessageStatus}
                                    openFullscreenImage={(url) => dispatch(setFullscreenImage(url))}
                                    user={user}
                                    selectedMessageId={null}
                                    onDelete={handleDeleteMessage}
                                    isSelected={selectedMessages.some(msg => msg.id === message.id)}
                                    isActiveThread={message.id === selectedMessageId}
                                    isThreadOpen={false}
                                    onSelect={handleMessageSelect}
                                    showAuthor={shouldShowAuthor(message, index > 0 ? threadMessages[index - 1] : null)}
                                    showAvatar={isLastInSeries(message, index < threadMessages.length - 1 ? threadMessages[index + 1] : null)}
                                    onFocus={handleMessageFocus}
                                    isFocused={focusedMessageIndex === index}
                                    mobileScreen={mobileScreen}
                                    onReply={handleMessageReply}
                                    isReplySelected={selectedReplyMessage?.id === message.id}
                                    onScrollToMessage={handleScrollToMessage}
                                />
                            </div>
                        </React.Fragment>
                    ))}

                    {/* No replies yet indicator */}
                    {threadMessages.length === 0 && !isLoadingMore && parentMessage && (
                        <div className="flex flex-col items-center justify-center py-8 text-center">
                            <MessageSquare className="h-12 w-12 text-muted-foreground/30 mb-3" />
                            <p className="text-sm text-muted-foreground">No replies yet</p>
                            <p className="text-xs text-muted-foreground/60 mt-1">Be the first to reply!</p>
                        </div>
                    )}
                </div>

                {/* Multi-select toolbar */}
                {selectedMessages.length > 0 && (
                    <div className="fixed bottom-20 right-5 z-50 bg-card border rounded-lg shadow-lg p-3 flex items-center gap-3">
                        <Badge variant="default" className="text-sm">
                            {selectedMessages.length} selected
                        </Badge>
                        <Button
                            size="sm"
                            onClick={handleCopySelectedToCanvas}
                            className="gap-2"
                        >
                            <Copy className="h-4 w-4" />
                            Link to Canvas
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleForwardClick}
                            className="gap-2"
                            disabled={selectedMessages.length > 30}
                        >
                            <Forward className="h-4 w-4" />
                            Forward
                        </Button>
                        <Button
                            size="sm"
                            variant="destructive"
                            onClick={handleDeleteSelected}
                            className="gap-2"
                        >
                            <Trash2 className="h-4 w-4" />
                            Delete
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleClearSelection}
                            className="gap-2"
                        >
                            <X className="h-4 w-4" />
                            Clear
                        </Button>
                    </div>
                )}
            </div>

            {/* Message composer */}
            <div className="shrink-0">
                <CreateMessage
                    messages={threadMessages}
                    addMessage={addThreadMessage}
                    channelId={channelId}
                    threadId={activeThread}
                    parentMessageId={parentMessageId}
                    repliedToMessage={selectedReplyMessage}
                    onCancelReply={handleCancelReply}
                />
            </div>

            {/* Forward Message Dialog */}
            <ForwardMessageDialog
                open={forwardDialogOpen}
                onOpenChange={setForwardDialogOpen}
                selectedMessages={selectedMessages}
                onForwardSuccess={() => {
                    setSelectedMessages([]);
                }}
            />
        </div>
    );
};

export default ThreadView;
